// xterm.js terminal wrapper

import { Terminal, ITheme } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { WebglAddon } from '@xterm/addon-webgl';
import { Unicode11Addon } from '@xterm/addon-unicode11';

export const TERMINAL_FONT_FAMILY = '"JetBrains Mono", "Fira Code", "SF Mono", Menlo, Monaco, "Courier New", monospace';
export const TERMINAL_FONT_SIZE = 14;
export const TERMINAL_THEME: ITheme = {
  background: '#000000',
  foreground: '#c0c0c0',
  cursor: '#c0c0c0',
  cursorAccent: '#000000',
  selectionBackground: '#404040',
  selectionForeground: '#ffffff',
  black: '#000000',
  red: '#cd0000',
  green: '#00cd00',
  yellow: '#cdcd00',
  blue: '#0000cd',
  magenta: '#cd00cd',
  cyan: '#00cdcd',
  white: '#c0c0c0',
  brightBlack: '#404040',
  brightRed: '#ff0000',
  brightGreen: '#00ff00',
  brightYellow: '#ffff00',
  brightBlue: '#0000ff',
  brightMagenta: '#ff00ff',
  brightCyan: '#00ffff',
  brightWhite: '#ffffff',
};

export interface TerminalOptions {
  container: HTMLElement;
  onInput?: (data: string) => void;
  onResize?: (cols: number, rows: number) => void;
}

export class LeraTerminal {
  private terminal: Terminal;
  private fitAddon: FitAddon;
  private webglAddon: WebglAddon | null = null;
  private unicode11Addon: Unicode11Addon | null = null;
  private container: HTMLElement;
  private resizeObserver: ResizeObserver;
  private onResizeCallback?: (cols: number, rows: number) => void;
  private lastCols: number = 0;
  private lastRows: number = 0;

  constructor(options: TerminalOptions) {
    this.container = options.container;

    // Create terminal with settings matching typical MUD output
    this.terminal = new Terminal({
      cursorBlink: true,
      cursorStyle: 'block',
      fontFamily: TERMINAL_FONT_FAMILY,
      fontSize: TERMINAL_FONT_SIZE,
      lineHeight: 1.2,
      letterSpacing: 0,
      allowProposedApi: true,
      scrollback: 10000,
      theme: TERMINAL_THEME,
    });

    // Load fit addon for responsive sizing
    this.fitAddon = new FitAddon();
    this.terminal.loadAddon(this.fitAddon);

    // Load Unicode11 addon for proper handling of box-drawing characters
    // and other Unicode characters with ambiguous widths
    this.unicode11Addon = new Unicode11Addon();
    this.terminal.loadAddon(this.unicode11Addon);
    this.terminal.unicode.activeVersion = '11';

    // Open terminal in container
    this.terminal.open(this.container);

    // Try to load WebGL addon for better performance
    try {
      this.webglAddon = new WebglAddon();
      this.webglAddon.onContextLoss(() => {
        this.webglAddon?.dispose();
        this.webglAddon = null;
      });
      this.terminal.loadAddon(this.webglAddon);
    } catch (e) {
      console.warn('[terminal] WebGL not available, using canvas renderer');
    }

    // Handle input
    if (options.onInput) {
      this.terminal.onData(options.onInput);
    }

    // Store resize callback
    this.onResizeCallback = options.onResize;

    // Auto-resize on container size change
    this.resizeObserver = new ResizeObserver(() => {
      this.fit();
    });
    this.resizeObserver.observe(this.container);

    // Initial fit
    this.fit();
  }

  write(data: string): void {
    this.terminal.write(data);
  }

  writeln(data: string): void {
    this.terminal.writeln(data);
  }

  clear(): void {
    this.terminal.clear();
  }

  fit(): void {
    try {
      this.fitAddon.fit();
      // Notify if dimensions changed
      if (this.onResizeCallback &&
          (this.terminal.cols !== this.lastCols || this.terminal.rows !== this.lastRows)) {
        this.lastCols = this.terminal.cols;
        this.lastRows = this.terminal.rows;
        this.onResizeCallback(this.terminal.cols, this.terminal.rows);
      }
    } catch (e) {
      // Ignore fit errors (can happen during initialization)
    }
  }

  focus(): void {
    this.terminal.focus();
  }

  blur(): void {
    this.terminal.blur();
  }

  get rows(): number {
    return this.terminal.rows;
  }

  get cols(): number {
    return this.terminal.cols;
  }

  dispose(): void {
    this.resizeObserver.disconnect();
    this.webglAddon?.dispose();
    this.unicode11Addon?.dispose();
    this.fitAddon.dispose();
    this.terminal.dispose();
  }

  // Scroll to bottom (useful after receiving output)
  scrollToBottom(): void {
    this.terminal.scrollToBottom();
  }

  // Set font size
  setFontSize(size: number): void {
    this.terminal.options.fontSize = size;
    this.fit();
  }

  // Resize terminal to specific dimensions
  resize(cols: number, rows: number): void {
    this.terminal.resize(cols, rows);
  }

  // Get underlying terminal for advanced usage
  get raw(): Terminal {
    return this.terminal;
  }
}
